import React, { useState, useMemo } from "react";
import { useValidationToast, validateFields } from "../utils/validation";
import { fmtSAR, fmtCompact, TODAY } from "../utils/format";
import { getStaffApprovers } from "../permissions/checks";
import { fireNotification } from "../notifications";
import Modal from "../shared/Modal";
import useBnavFab from "../shared/useBnavFab";

/* Shared helper — also consumed by StaffModule (owner/HR view) and the
   Leave panel. Keep this export intact; only the default-export panel
   changes visually. */
export function submitLeaveShared({leaveForm,staffRecord,state,dispatch,platformCtx,isRTL}){
  if(!leaveForm.startDate||!leaveForm.endDate||!staffRecord)return false;
  const req={...leaveForm,id:`lr${Date.now()}`,staffId:staffRecord.id,staffName:staffRecord.name,status:"pending",requestedAt:TODAY};
  dispatch({type:"ADD_LEAVE_REQ",req});
  const approvers=getStaffApprovers("leaveApprovers",state,platformCtx);
  approvers.forEach(u=>{
    dispatch({type:"ADD_NOTIF",notif:{id:`n${Date.now()}_${u.id}`,userId:u.id,type:"leave",
      title:isRTL?"طلب إجازة":"Leave request",
      body:`${isRTL&&staffRecord.nameAr?staffRecord.nameAr:staffRecord.name} · ${leaveForm.startDate} → ${leaveForm.endDate}`,
      link:"staff",read:false,time:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),date:TODAY}});
  });
  fireNotification({eventKey:"leave_requested",state,dispatch,platformCtx,
    link:"staff",eventData:{staffName:staffRecord.name,startDate:leaveForm.startDate,endDate:leaveForm.endDate}});
  return true;
}

/* ══ MY FINANCES PANEL ══════════════════════════════════════
   Staff-side personal finance surface. Visual language matches the staff
   Expenses and Suppliers panels:
     – `.exp-hero` title + subtitle + `.exp-summary-glass` KPI card
     – `.sup-status-tabs` segmented control to filter loan requests
     – `.exp-merged-card` list rows (.exp-row) with status pills
     – `.stf-detail-overlay` bottom-sheet for loan-request detail + create

   The bottom-nav "+" FAB (see StaffView manifest: hasFab:true) dispatches
   `bnav:fab`; useBnavFab catches it here and opens the loan-request modal.
   This mirrors the Expenses / Leave pattern — the FAB is the single add
   affordance on every staff panel. */
export default function StaffSelfInfoPanel({staffRecord,loanRequests,activeLoanDeductions,netSalary,myLoans,dispatch,users,staffAuthorities,isRTL}){
  const vt=useValidationToast();
  const [showLoanReq,setShowLoanReq]=useState(false);
  const [loanReqForm,setLoanReqForm]=useState({description:"",amount:"",deductMonths:"1",notes:""});
  const [statusFilter,setStatusFilter]=useState("All");
  const [detailReq,setDetailReq]=useState(null);

  /* FAB — mounted before the early return so the hook order is stable
     even if the user has no staff record linked. */
  useBnavFab(()=>{if(staffRecord)setShowLoanReq(true);});

  const myLoanRequests=useMemo(
    ()=>(loanRequests||[]).filter(r=>r.staffId===staffRecord?.id),
    [loanRequests,staffRecord]
  );
  const statusCounts=useMemo(()=>({
    All:myLoanRequests.length,
    Pending:myLoanRequests.filter(r=>r.status==="pending").length,
    Approved:myLoanRequests.filter(r=>r.status==="approved").length,
    Rejected:myLoanRequests.filter(r=>r.status==="rejected").length,
  }),[myLoanRequests]);
  const filteredReqs=statusFilter==="All"
    ?myLoanRequests
    :myLoanRequests.filter(r=>r.status===statusFilter.toLowerCase());

  if(!staffRecord){
    return(
      <div className="page inc-page-bg">
        <div className="stf-hero">
          <div className="stf-hero-greeting">{isRTL?"ماليتي":"My Finances"}</div>
          <div className="stf-hero-sub">{isRTL?"الراتب · السلف · كشوف الراتب":"Salary · Loans · Payslips"}</div>
        </div>
        <div className="notice" style={{margin:"12px 16px"}}>
          <div className="notice-l">{isRTL?"غير مرتبط":"Not Linked"}</div>
          {isRTL?"حسابك غير مرتبط بسجل موظف. تواصل مع مديرك.":"Your account is not linked to a staff record. Contact your manager to set this up."}
        </div>
      </div>
    );
  }

  const submitLoanRequest=()=>{
    const m=validateFields([{ok:loanReqForm.description.trim(),label:isRTL?"الوصف":"Description"},{ok:loanReqForm.amount,label:isRTL?"المبلغ":"Amount"}]);
    if(m.length){vt.show(m.join(", "));return;}
    const req={
      id:`lr_loan_${Date.now()}`,
      staffId:staffRecord.id,
      staffName:staffRecord.name,
      description:loanReqForm.description.trim(),
      amount:parseFloat(loanReqForm.amount)||0,
      deductMonths:parseInt(loanReqForm.deductMonths)||1,
      notes:loanReqForm.notes.trim(),
      status:"pending",
      requestedAt:TODAY,
    };
    dispatch({type:"ADD_LOAN_REQUEST",req});
    const approverIds=staffAuthorities?.loanApprovers||[];
    users.filter(u=>u.active&&(u.role==="owner"||approverIds.includes(u.id))).forEach(u=>{
      dispatch({type:"ADD_NOTIF",notif:{
        id:`n_loan_${Date.now()}_${u.id}`,userId:u.id,type:"loan",
        title:isRTL?"طلب سلفة / قرض":"Loan / Advance Request",
        body:`${staffRecord.name} · SAR ${req.amount} · ${req.description}`,
        link:"staff",read:false,
        time:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),date:TODAY,
      }});
    });
    setShowLoanReq(false);
    setLoanReqForm({description:"",amount:"",deductMonths:"1",notes:""});
  };

  const grossSalary=staffRecord.salary||0;
  const activeLoanCount=myLoans.filter(l=>l.status==="active").length;
  const payslips=staffRecord.payslips||[];

  const statusBadge=(s)=>s==="approved"?"status-a":s==="rejected"?"status-rej":"status-p";
  const statusLabel=(s)=>isRTL?(s==="approved"?"موافق":s==="rejected"?"مرفوض":"معلق"):s.charAt(0).toUpperCase()+s.slice(1);
  const monthly=(amt,m)=>Math.round((amt/(m||1))*100)/100;

  const STATUSES=["All","Pending","Approved","Rejected"];
  const statusTabLabel=(s)=>{
    if(!isRTL)return s;
    return s==="All"?"الكل":s==="Pending"?"معلق":s==="Approved"?"موافق":"مرفوض";
  };

  return(
    <div className="page inc-page-bg">
      {vt.Toast}

      {/* ═══ MOBILE: Hero — matches Tasks/Suppliers vocabulary ═══ */}
      <div className="stf-hero">
        <div className="stf-hero-greeting">{isRTL?"ماليتي":"My Finances"}</div>
        <div className="stf-hero-sub">{isRTL?"الراتب · السلف · كشوف الراتب":"Salary · Loans · Payslips"}</div>
        <div className="stf-summary-glass">
          <div className="stf-summary-row">
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:"var(--ink)"}}>{fmtCompact(netSalary||0)}</div><div className="stf-summary-lbl">{isRTL?"صافي":"Net"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:"var(--grn)"}}>{fmtCompact(grossSalary)}</div><div className="stf-summary-lbl">{isRTL?"إجمالي":"Gross"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:activeLoanDeductions?"var(--red)":"var(--g400)"}}>{fmtCompact(activeLoanDeductions||0)}</div><div className="stf-summary-lbl">{isRTL?"خصومات":"Deductions"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:activeLoanCount?"var(--gold)":"var(--g400)"}}>{activeLoanCount}</div><div className="stf-summary-lbl">{isRTL?"سلف":"Loans"}</div></div>
          </div>
        </div>
      </div>

      {/* Status tabs — filter loan requests */}
      <div className="exp-status-tabs">
        {STATUSES.map(s=>(
          <button key={s} className={`exp-status-tab ${statusFilter===s?"active":""}`} onClick={()=>setStatusFilter(s)}>
            {statusTabLabel(s)}{statusCounts[s]>0?` · ${statusCounts[s]}`:""}
          </button>
        ))}
      </div>

      {/* ═══ LOAN REQUESTS — primary list ═══ */}
      <div style={{padding:"0 16px",marginBottom:18}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",margin:"4px 2px 10px"}}>
          {isRTL?"طلبات السلفة / القرض":"Loan & Advance Requests"}
        </div>
        {filteredReqs.length>0?(
          <div className="exp-merged-card">
            {filteredReqs.map((r,i)=>(
              <div key={r.id} className="exp-row" style={{borderBottom:i<filteredReqs.length-1?"1px solid var(--g100)":"none"}} onClick={()=>setDetailReq(r)}>
                <div className="exp-row-icon" style={{background:r.status==="approved"?"var(--grn-bg)":r.status==="rejected"?"var(--red-bg)":"var(--gold-bg)"}}>
                  <span style={{color:r.status==="approved"?"var(--grn)":r.status==="rejected"?"var(--red)":"var(--gold)",fontSize:15,fontWeight:700}}>₣</span>
                </div>
                <div className="exp-row-info">
                  <div className="exp-row-name">{r.description}</div>
                  <div className="exp-row-sub">{fmtSAR(r.amount)} · {fmtSAR(monthly(r.amount,r.deductMonths))}{isRTL?"/شهر":"/mo"} × {r.deductMonths} · {r.requestedAt}</div>
                </div>
                <span className={`exp-m-badge ${statusBadge(r.status)}`} style={{flexShrink:0}}>{statusLabel(r.status)}</span>
              </div>
            ))}
          </div>
        ):(
          <div className="exp-merged-card" style={{textAlign:"center",padding:"28px 20px",color:"var(--g400)",fontSize:13}}>
            {statusFilter==="All"
              ?(isRTL?"لا طلبات بعد — اضغط + لطلب سلفة":"No loan requests yet — tap + to request one")
              :(isRTL?"لا نتائج بهذا الفلتر":"No requests match this filter")}
          </div>
        )}
      </div>

      {/* ═══ ACTIVE LOANS ═══ */}
      {myLoans.length>0&&(
        <div style={{padding:"0 16px",marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",margin:"4px 2px 10px"}}>
            {isRTL?"السلف النشطة":"Active Loans"}
          </div>
          <div className="exp-merged-card">
            {myLoans.map((l,i)=>(
              <div key={l.id} className="exp-row" style={{borderBottom:i<myLoans.length-1?"1px solid var(--g100)":"none",cursor:"default"}}>
                <div className="exp-row-icon" style={{background:l.status==="active"?"var(--gold-bg)":"var(--g100)"}}>
                  <span style={{color:l.status==="active"?"var(--gold)":"var(--g500)",fontSize:15,fontWeight:700}}>L</span>
                </div>
                <div className="exp-row-info">
                  <div className="exp-row-name">{l.description}</div>
                  <div className="exp-row-sub">{fmtSAR(monthly(l.amount,l.deductMonths))}{isRTL?"/شهر":"/mo"} × {l.deductMonths} {isRTL?"أشهر":"months"} · {fmtSAR(l.amount)}</div>
                </div>
                <span className={`pill ${l.status==="active"?"p-wait":"p-done"}`} style={{flexShrink:0}}>
                  {l.status==="active"?(isRTL?"نشط":"Active"):(isRTL?"مسوّى":"Settled")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ PAYSLIPS ═══ */}
      {payslips.length>0&&(
        <div style={{padding:"0 16px",marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",margin:"4px 2px 10px"}}>
            {isRTL?"كشوف الراتب":"Payslips"}
          </div>
          <div className="exp-merged-card">
            {payslips.slice(-6).reverse().map((ps,i,arr)=>(
              <div key={ps.id} className="exp-row" style={{borderBottom:i<arr.length-1?"1px solid var(--g100)":"none",cursor:"default"}}>
                <div className="exp-row-icon" style={{background:ps.paid?"var(--grn-bg)":"var(--gold-bg)"}}>
                  <span style={{color:ps.paid?"var(--grn)":"var(--gold)",fontSize:15,fontWeight:700}}>¶</span>
                </div>
                <div className="exp-row-info">
                  <div className="exp-row-name">{ps.month}</div>
                  <div className="exp-row-sub">{isRTL?"صافي":"Net"} {fmtSAR(ps.net)}</div>
                </div>
                <span className={`pill ${ps.paid?"p-done":"p-wait"}`} style={{flexShrink:0}}>
                  {ps.paid?(isRTL?"مدفوع":"Paid"):(isRTL?"معلق":"Pending")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ SALARY BREAKDOWN (detail-section style) ═══ */}
      <div style={{padding:"0 16px 24px"}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",margin:"4px 2px 10px"}}>
          {isRTL?"تفاصيل الراتب":"Salary Details"}
        </div>
        <div className="exp-merged-card" style={{padding:"4px 16px"}}>
          {[
            [isRTL?"الراتب الإجمالي":"Gross Salary",   fmtSAR(grossSalary)],
            [isRTL?"الاستقطاعات":"Deductions",         fmtSAR(activeLoanDeductions||0)],
            [isRTL?"صافي الراتب":"Net Salary",         fmtSAR(netSalary||0)],
            [isRTL?"الدورية":"Frequency",              staffRecord.frequency||"—"],
            [isRTL?"تاريخ البدء":"Start Date",         staffRecord.startDate||"—"],
          ].map(([k,v])=>(
            <div key={k} className="exp-detail-row"><span className="exp-detail-row-label">{k}</span><span className="exp-detail-row-value mono">{v}</span></div>
          ))}
        </div>
      </div>

      {/* ═══ LOAN REQUEST DETAIL SHEET ═══ */}
      {detailReq&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setDetailReq(null)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{detailReq.description}</div>
              <button className="stf-detail-close" onClick={()=>setDetailReq(null)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="exp-detail-amount" style={{color:detailReq.status==="approved"?"var(--grn)":detailReq.status==="rejected"?"var(--red)":"var(--gold)"}}>{fmtSAR(detailReq.amount)}</div>
              <div className="exp-detail-badges">
                <span className={`exp-detail-badge ${statusBadge(detailReq.status)}`} style={{background:detailReq.status==="approved"?"var(--grn-bg)":detailReq.status==="rejected"?"var(--red-bg)":"var(--gold-bg)",color:detailReq.status==="approved"?"var(--grn)":detailReq.status==="rejected"?"var(--red)":"var(--gold)"}}>{statusLabel(detailReq.status)}</span>
              </div>
              <div className="exp-detail-section">
                <div className="exp-detail-section-title">{isRTL?"التفاصيل":"Details"}</div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"المبلغ":"Amount"}</span><span className="exp-detail-row-value mono">{fmtSAR(detailReq.amount)}</span></div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"عدد الأشهر":"Over"}</span><span className="exp-detail-row-value">{detailReq.deductMonths} {isRTL?"أشهر":"months"}</span></div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"الاستقطاع الشهري":"Monthly deduction"}</span><span className="exp-detail-row-value mono">{fmtSAR(monthly(detailReq.amount,detailReq.deductMonths))}</span></div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"تاريخ الطلب":"Requested"}</span><span className="exp-detail-row-value mono">{detailReq.requestedAt}</span></div>
              </div>
              {detailReq.notes&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"ملاحظات":"Notes"}</div>
                  <div className="exp-detail-notes">{detailReq.notes}</div>
                </div>
              )}
              {detailReq.status==="approved"&&detailReq.approvedBy&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"الموافقة":"Approval"}</div>
                  <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"موافق بواسطة":"Approved by"}</span><span className="exp-detail-row-value" style={{color:"var(--grn)"}}>{detailReq.approvedBy}</span></div>
                </div>
              )}
              {detailReq.status==="rejected"&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"الرفض":"Rejection"}</div>
                  {detailReq.rejectedBy&&<div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"مرفوض بواسطة":"Rejected by"}</span><span className="exp-detail-row-value" style={{color:"var(--red)"}}>{detailReq.rejectedBy}</span></div>}
                  {detailReq.rejectReason&&<div className="exp-detail-notes" style={{marginTop:10}}>{detailReq.rejectReason}</div>}
                </div>
              )}
            </div>
          </div>
        </div></Modal>
      )}

      {/* ═══ NEW LOAN REQUEST SHEET (opened by "+" FAB) ═══ */}
      {showLoanReq&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setShowLoanReq(false)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{isRTL?"طلب سلفة أو قرض":"New Loan / Advance"}</div>
              <button className="stf-detail-close" onClick={()=>setShowLoanReq(false)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fg" style={{marginBottom:12}}>
                <label className="fl">{isRTL?"السبب / الوصف":"Description / Reason"}</label>
                <input className="fi" value={loanReqForm.description} onChange={e=>setLoanReqForm(p=>({...p,description:e.target.value}))}
                  placeholder={isRTL?"مثال: سلفة شخصية، طارئ طبي…":"e.g. Personal advance, medical emergency…"} autoFocus/>
              </div>
              <div className="fr">
                <div className="fg">
                  <label className="fl">{isRTL?"المبلغ (ريال)":"Amount (SAR)"}</label>
                  <input className="fi" type="number" value={loanReqForm.amount} onChange={e=>setLoanReqForm(p=>({...p,amount:e.target.value}))} min="0"/>
                </div>
                <div className="fg">
                  <label className="fl">{isRTL?"الاستقطاع على (أشهر)":"Deduct over (months)"}</label>
                  <input className="fi" type="number" value={loanReqForm.deductMonths} onChange={e=>setLoanReqForm(p=>({...p,deductMonths:e.target.value}))} min="1"/>
                </div>
              </div>
              {loanReqForm.amount&&loanReqForm.deductMonths&&(
                <div style={{fontSize:12,color:"var(--g500)",margin:"6px 0 10px"}}>
                  → {isRTL?"سيتم استقطاع":"Monthly deduction:"} {fmtSAR(monthly(parseFloat(loanReqForm.amount)||0,parseInt(loanReqForm.deductMonths)||1))} {isRTL?"شهرياً":"from each payroll"}
                </div>
              )}
              <div className="fg" style={{marginBottom:12}}>
                <label className="fl">{isRTL?"ملاحظات إضافية":"Additional notes"} <span style={{color:"var(--g400)",fontWeight:400}}>{isRTL?"(اختياري)":"(optional)"}</span></label>
                <textarea className="fta" value={loanReqForm.notes} onChange={e=>setLoanReqForm(p=>({...p,notes:e.target.value}))} style={{minHeight:64}}/>
              </div>
            </div>
            <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
              <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowLoanReq(false)}>{isRTL?"إلغاء":"Cancel"}</button>
              <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={submitLoanRequest}>{isRTL?"إرسال الطلب":"Submit Request"}</button>
            </div>
          </div>
        </div></Modal>
      )}
    </div>
  );
}
